import { format } from 'date-fns';
import { AlertTriangleIcon, RefreshCcwIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle, Button } from '@tinct/web-components';
import type { LinkedinTokenState } from '../../../components/linkedin-ads-token-state';

type ReauthorizationBannerProps = {
  tokenState: LinkedinTokenState;
  onReconnect: () => void;
  isReconnecting?: boolean;
};

/**
 * LinkedIn tokens die after 60 days with no refresh: warn a week ahead, and
 * once the connection is dead say so. "Reconnect" reuses the OAuth popup —
 * settings, synced campaigns and metrics survive it.
 */
export function ReauthorizationBanner({
  tokenState,
  onReconnect,
  isReconnecting = false,
}: ReauthorizationBannerProps) {
  if (tokenState.kind === 'ok') return null;

  const expired = tokenState.kind === 'expired';
  const expiresOn = tokenState.expiresAt
    ? format(tokenState.expiresAt, 'PPP')
    : null;

  return (
    <Alert
      variant={expired ? 'destructive' : 'default'}
      data-testid="linkedin-reauthorization-banner"
      className={expired ? undefined : 'border-amber-300 bg-amber-50 text-amber-900'}
    >
      <AlertTriangleIcon />
      <AlertTitle>
        {expired
          ? 'LinkedIn access expired'
          : `LinkedIn access expires on ${expiresOn}`}
      </AlertTitle>
      <AlertDescription className={expired ? undefined : 'text-amber-900/80'}>
        <p>
          {expired
            ? 'Campaigns and metrics stopped syncing. Reconnect your LinkedIn account to resume — your account selection, synced campaigns and matches are kept.'
            : `LinkedIn tokens last 60 days and cannot be refreshed automatically. Reconnect before then (${tokenState.daysLeft} day${tokenState.daysLeft === 1 ? '' : 's'} left) to keep campaigns and metrics syncing.`}
        </p>
        <Button
          size="sm"
          variant={expired ? 'destructive' : 'outline'}
          onClick={onReconnect}
          isLoading={isReconnecting}
          className="mt-1"
        >
          {!isReconnecting && <RefreshCcwIcon className="size-4" />}
          Reconnect
        </Button>
      </AlertDescription>
    </Alert>
  );
}
